import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { openWhatsApp } from "@/lib/whatsapp";
import {
  Plus,
  Trash2,
  QrCode,
  Upload,
  Check,
  X,
  Clock,
  CheckCircle2,
  XCircle,
  Eye,
  MessageCircle,
  Download,
} from "lucide-react";

const PAYMENT_METHODS = ["UPI", "Cash", "Bank Transfer", "Cheque", "Card"];

const emptyForm = {
  amount: "",
  vendor: "",
  description: "",
  qr_image: "",
  due_date: "",
  payment_method: "UPI",
};

function fmtINR(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

const STATUS_STYLES = {
  pending: { bg: "bg-accent/20 text-accent-foreground", icon: Clock, label: "Pending" },
  paid: { bg: "bg-emerald-100 text-emerald-800", icon: CheckCircle2, label: "Paid" },
  rejected: { bg: "bg-red-100 text-red-800", icon: XCircle, label: "Rejected" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs uppercase tracking-widest ${s.bg}`}>
      <Icon className="w-3 h-3" aria-hidden="true" />
      {s.label}
    </span>
  );
}

export default function PaymentsPage() {
  const { user } = useAuth();
  const isManager = Boolean(user?.manager_name);
  const isOwner = user?.role === "admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [ownerPhone, setOwnerPhone] = useState("");

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const [preview, setPreview] = useState(null); // request for image preview
  const [decision, setDecision] = useState(null); // {row, action: "paid"|"rejected"}
  const [note, setNote] = useState("");
  const [deciding, setDeciding] = useState(false);

  const [toDelete, setToDelete] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/payment-requests", { params });
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api.get("/settings/public")
      .then(({ data }) => setOwnerPhone(data.owner_phone || ""))
      .catch(() => {});
  }, []);

  const downloadMonthly = async () => {
    setDownloading(true);
    try {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth() + 1;
      const params = { year, month };
      if (filter !== "all") params.status = filter;
      const res = await api.get("/payment-requests/monthly-pdf", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `royal-wow-payments-${year}-${String(month).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded payments PDF for ${year}-${String(month).padStart(2, "0")}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Download failed");
    } finally { setDownloading(false); }
  };

  const openNew = () => {
    setForm(emptyForm);
    setOpen(true);
  };

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image (PNG or JPG)");
      return;
    }
    if (file.size > 700 * 1024) {
      toast.error("QR image must be under 700 KB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, qr_image: reader.result }));
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!form.vendor.trim()) {
      toast.error("Vendor name is required");
      return;
    }
    setSaving(true);
    try {
      await api.post("/payment-requests", {
        amount: parseFloat(form.amount),
        vendor: form.vendor.trim(),
        description: form.description,
        qr_image: form.qr_image || null,
        due_date: form.due_date || null,
        payment_method: form.payment_method,
      });
      toast.success("Payment request sent to owner", {
        action: {
          label: "Notify on WhatsApp",
          onClick: () => {
            const msg = `Royal Wow Meals: ${user?.manager_name || "Manager"} has raised a payment request of ₹${form.amount} for ${form.vendor.trim()}. ${form.description ? "Note: " + form.description : ""} Please review.`.trim();
            openWhatsApp(ownerPhone, msg);
          },
        },
      });
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to send request");
    } finally {
      setSaving(false);
    }
  };

  const doDecision = async () => {
    if (!decision) return;
    setDeciding(true);
    try {
      await api.patch(`/payment-requests/${decision.row.id}/decision`, {
        status: decision.action,
        owner_note: note,
      });
      toast.success(decision.action === "paid" ? "Marked paid — expense recorded" : "Request rejected");
      setDecision(null);
      setNote("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed");
    } finally {
      setDeciding(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/payment-requests/${toDelete.id}`);
      toast.success("Request removed");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const pending = rows.filter((r) => r.status === "pending").length;
  const paid = rows.filter((r) => r.status === "paid").length;
  const rejected = rows.filter((r) => r.status === "rejected").length;

  return (
    <div data-testid="payments-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Finance</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2">Bill payment requests</h1>
          <p className="mt-3 text-muted-foreground">
            {isOwner
              ? "Vendors and bills your managers need you to pay. Review the QR, pay from your UPI app, then mark done."
              : "Send a bill to the owner. Attach the vendor's QR code and the amount — the owner will pay from their phone."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={downloadMonthly}
            disabled={downloading}
            data-testid="download-monthly-payments-btn"
            variant="outline"
            className="rounded-full"
          >
            <Download className="w-4 h-4 mr-2" aria-hidden="true" />
            {downloading ? "Preparing…" : "Monthly PDF"}
          </Button>
          {isManager && (
            <Button
              onClick={openNew}
              data-testid="new-payment-request-btn"
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> New request
            </Button>
          )}
        </div>
      </div>

      {/* Stat pills */}
      <div className="mt-8 grid grid-cols-3 gap-4 max-w-3xl">
        <div className="bg-white border border-border p-4 fade-up" data-testid="stat-pending">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Pending</div>
          <div className="mt-1 font-serif text-3xl">{pending}</div>
        </div>
        <div className="bg-white border border-border p-4 fade-up" data-testid="stat-paid">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Paid</div>
          <div className="mt-1 font-serif text-3xl">{paid}</div>
        </div>
        <div className="bg-white border border-border p-4 fade-up" data-testid="stat-rejected">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Rejected</div>
          <div className="mt-1 font-serif text-3xl">{rejected}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="mt-6 flex items-center gap-3">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Show</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="payments-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="mt-4 grid grid-cols-1 gap-4">
        {loading ? (
          <div className="bg-white border border-border p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-border p-14 text-center">
            <p className="font-serif text-2xl">No payment requests yet.</p>
            <p className="text-muted-foreground mt-2">
              {isManager
                ? "Tap 'New request' to send your first bill to the owner."
                : "When a manager needs a bill paid, it will appear here."}
            </p>
          </div>
        ) : (
          rows.map((r) => (
            <div
              key={r.id}
              className="bg-white border border-border p-6 fade-up"
              data-testid={`payment-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-3xl tracking-tight">{fmtINR(r.amount)}</span>
                    <StatusBadge status={r.status} />
                    <span className="text-xs text-muted-foreground">via {r.payment_method}</span>
                  </div>
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Vendor:</span>{" "}
                    <span className="font-medium">{r.vendor}</span>
                  </div>
                  {r.description && (
                    <p className="mt-1 text-sm text-muted-foreground">{r.description}</p>
                  )}
                  <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                    <span>Raised by <strong>{r.manager}</strong> · {fmtDateTime(r.created_at)}</span>
                    {r.due_date && <span>Due {r.due_date}</span>}
                    {r.status !== "pending" && (
                      <span>
                        {r.status === "paid" ? "Paid" : "Rejected"} on {fmtDateTime(r.decided_at)}
                      </span>
                    )}
                  </div>
                  {r.owner_note && (
                    <div className="mt-3 text-xs bg-secondary px-3 py-2 rounded">
                      <span className="text-muted-foreground uppercase tracking-widest">Owner note:</span>{" "}
                      {r.owner_note}
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-3">
                  {r.qr_image ? (
                    <button
                      type="button"
                      onClick={() => setPreview(r)}
                      data-testid={`view-qr-${r.id}`}
                      className="group relative w-24 h-24 bg-secondary border border-border overflow-hidden"
                    >
                      <img src={r.qr_image} alt="QR" className="w-full h-full object-contain" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Eye className="w-5 h-5 text-white" aria-hidden="true" />
                      </div>
                    </button>
                  ) : (
                    <div className="w-24 h-24 bg-secondary border border-dashed border-border flex items-center justify-center text-muted-foreground">
                      <QrCode className="w-6 h-6" aria-hidden="true" />
                    </div>
                  )}

                  {isOwner && r.status === "pending" && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setDecision({ row: r, action: "rejected" }); setNote(""); }}
                        data-testid={`reject-${r.id}`}
                      >
                        <X className="w-4 h-4 mr-1" aria-hidden="true" /> Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => { setDecision({ row: r, action: "paid" }); setNote(""); }}
                        data-testid={`approve-${r.id}`}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <Check className="w-4 h-4 mr-1" aria-hidden="true" /> Mark paid
                      </Button>
                    </div>
                  )}

                  {(isOwner || (isManager && r.status === "pending" && r.manager === user.manager_name)) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setToDelete(r)}
                      data-testid={`delete-payment-${r.id}`}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* New request dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-payment-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">New payment request</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount">Amount (₹)</Label>
                <Input
                  id="amount"
                  data-testid="pr-amount"
                  type="number"
                  step="0.01"
                  min="0.01"
                  required
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                />
              </div>
              <div>
                <Label>Method</Label>
                <Select
                  value={form.payment_method}
                  onValueChange={(v) => setForm({ ...form, payment_method: v })}
                >
                  <SelectTrigger data-testid="pr-method">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="vendor">Vendor / Payee *</Label>
              <Input
                id="vendor"
                data-testid="pr-vendor"
                required
                value={form.vendor}
                onChange={(e) => setForm({ ...form, vendor: e.target.value })}
                placeholder="e.g. Sabzi Mandi Ram Prasad"
              />
            </div>

            <div>
              <Label htmlFor="due">Due date (optional)</Label>
              <Input
                id="due"
                data-testid="pr-due"
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>

            <div>
              <Label htmlFor="desc">Notes for the owner</Label>
              <Textarea
                id="desc"
                data-testid="pr-desc"
                rows={2}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="e.g. Weekly vegetables — invoice #234"
              />
            </div>

            <div>
              <Label>Vendor's QR code (image)</Label>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onFilePicked}
                data-testid="pr-qr-input"
              />
              <div className="mt-2 flex items-center gap-4">
                {form.qr_image ? (
                  <img
                    src={form.qr_image}
                    alt="QR preview"
                    className="w-28 h-28 object-contain border border-border bg-secondary"
                  />
                ) : (
                  <div className="w-28 h-28 flex items-center justify-center border border-dashed border-border bg-secondary text-muted-foreground">
                    <QrCode className="w-8 h-8" aria-hidden="true" />
                  </div>
                )}
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    data-testid="pr-qr-choose"
                    className="rounded-full"
                  >
                    <Upload className="w-4 h-4 mr-2" aria-hidden="true" />
                    {form.qr_image ? "Replace image" : "Upload QR"}
                  </Button>
                  {form.qr_image && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setForm({ ...form, qr_image: "" })}
                      className="text-destructive hover:text-destructive"
                    >
                      Remove
                    </Button>
                  )}
                  <p className="text-xs text-muted-foreground max-w-[180px]">
                    PNG or JPG under 700 KB. Optional but recommended.
                  </p>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                type="submit"
                disabled={saving}
                data-testid="pr-submit-btn"
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
              >
                {saving ? "Sending…" : "Send to owner"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* QR preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-md" data-testid="qr-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {preview?.vendor} · {fmtINR(preview?.amount)}
            </DialogTitle>
          </DialogHeader>
          {preview?.qr_image && (
            <img
              src={preview.qr_image}
              alt="Payment QR"
              className="w-full h-auto max-h-[70vh] object-contain bg-secondary"
            />
          )}
          {preview?.description && (
            <p className="text-sm text-muted-foreground mt-2">{preview.description}</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Decision dialog */}
      <Dialog open={!!decision} onOpenChange={(o) => !o && setDecision(null)}>
        <DialogContent data-testid="decision-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight">
              {decision?.action === "paid" ? "Mark as paid" : "Reject request"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {decision?.action === "paid"
              ? `${fmtINR(decision?.row.amount)} to ${decision?.row.vendor}. This also creates an expense entry in the ledger under "Bill Payment" attributed to ${decision?.row.manager}.`
              : `Reject ${fmtINR(decision?.row.amount)} to ${decision?.row.vendor}. The manager will see your note.`}
          </p>
          <div>
            <Label htmlFor="note">Note for the manager (optional)</Label>
            <Textarea
              id="note"
              data-testid="decision-note"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={decision?.action === "paid" ? "UTR / reference number" : "Reason for rejection"}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              onClick={doDecision}
              disabled={deciding}
              data-testid="confirm-decision-btn"
              className={
                decision?.action === "paid"
                  ? "rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                  : "rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              }
            >
              {deciding ? "Working…" : decision?.action === "paid" ? "Confirm paid" : "Confirm reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif tracking-tight">Remove this request?</AlertDialogTitle>
            <AlertDialogDescription>
              Deletes {fmtINR(toDelete?.amount)} to {toDelete?.vendor}. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-payment-btn" onClick={doDelete}>
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
